import React from "react";
import { BookOpen, Users2, GraduationCap, ShieldCheck } from "lucide-react";
import { useLang } from "../context/LangContext";
import { FadeUp, Stagger, StaggerItem } from "../components/Motion";

export default function IIEAS() {
  const { t } = useLang();
  const products = [
    { Icon: BookOpen, h: t.iieas.product_1_h, d: t.iieas.product_1_d, num: "I" },
    { Icon: Users2, h: t.iieas.product_2_h, d: t.iieas.product_2_d, num: "II" },
    { Icon: GraduationCap, h: t.iieas.product_3_h, d: t.iieas.product_3_d, num: "III" },
  ];

  return (
    <div data-testid="page-iieas" className="paper-bg">
      {/* HEADER */}
      <section className="section-pad">
        <div className="page-container">
          <FadeUp>
            <p className="font-serif italic tracking-wider text-cerulean mb-6 text-xl md:text-2xl lg:text-[26px] leading-snug max-w-4xl" data-testid="iieas-eyebrow">{t.iieas.eyebrow}</p>
            <h1 className="iieas-display text-5xl md:text-6xl lg:text-7xl max-w-4xl leading-[1.05]">
              {t.iieas.title}
            </h1>
            <div className="mt-10 h-px w-24 bg-ink/60" />
            <p className="mt-10 text-base md:text-lg text-text-muted leading-relaxed max-w-3xl">
              {t.iieas.lead}
            </p>
          </FadeUp>
        </div>
      </section>

      <div className="page-container"><div className="h-px bg-ink/15" /></div>

      {/* OBJECTIVE */}
      <section className="section-pad">
        <div className="page-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-4">
            <div className="text-[11px] font-mono tracking-[0.22em] text-text-muted">SECCIÓN I</div>
            <h2 className="iieas-display text-3xl md:text-5xl mt-4">{t.iieas.objective_title}</h2>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-8">
            <p className="text-text-muted leading-relaxed text-base md:text-lg">
              {t.iieas.objective_p}
            </p>

            <div className="mt-10 space-y-7 border-l-2 border-cerulean/40 pl-7">
              <ObjBlock
                label={t.iieas.nature_label}
                text={t.iieas.nature_text}
                testid="iieas-nature"
              />
              <ObjBlock
                label={t.iieas.role_label}
                text={t.iieas.role_text}
                testid="iieas-role"
              />
              <ObjBlock
                label={t.iieas.sustainability_label}
                text={t.iieas.sustainability_text}
                testid="iieas-sustainability"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <div className="page-container"><div className="h-px bg-ink/15" /></div>

      {/* PRODUCTS */}
      <section className="section-pad">
        <div className="page-container">
          <FadeUp>
            <div className="text-[11px] font-mono tracking-[0.22em] text-text-muted">SECCIÓN II</div>
            <h2 className="iieas-display text-3xl md:text-5xl mt-4 max-w-3xl">{t.iieas.products_title}</h2>
          </FadeUp>

          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-ink/15">
            {products.map((p, i) => (
              <StaggerItem key={i}>
                <div className={`p-10 h-full border-b border-ink/15 ${i < 2 ? "md:border-r" : ""} md:border-ink/15 hover:bg-white/60 transition-colors`}>
                  <div className="flex items-center justify-between">
                    <span className="iieas-display text-3xl text-ink/40">{p.num}</span>
                    <p.Icon className="w-5 h-5 text-cerulean" strokeWidth={1.2} />
                  </div>
                  <h3 className="iieas-display text-2xl mt-12">{p.h}</h3>
                  <p className="mt-5 text-sm md:text-base text-text-muted leading-relaxed">
                    {p.d}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <div className="page-container"><div className="h-px bg-ink/15" /></div>

      {/* INDEPENDENCE */}
      <section className="section-pad">
        <div className="page-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <FadeUp className="lg:col-span-5">
            <div className="text-[11px] font-mono tracking-[0.22em] text-text-muted">SECCIÓN III</div>
            <h2 className="iieas-display text-3xl md:text-5xl mt-4">{t.iieas.independence_title}</h2>
            <div className="mt-8">
              <ShieldCheck className="w-8 h-8 text-cerulean" strokeWidth={1.2} />
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-7">
            <blockquote className="border-l-2 border-cerulean pl-8 text-text-muted leading-relaxed text-base md:text-lg">
              {t.iieas.independence_p}
            </blockquote>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}

function ObjBlock({ label, text, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-mono tracking-[0.22em] uppercase text-cerulean">{label}</div>
      <p className="mt-2 text-sm md:text-base text-ink leading-relaxed">{text}</p>
    </div>
  );
}
